// A flagship product told as a scroll story: the model turns as you scroll, the pitch lights up word by word,
// then features, the detail sections, numbers, film and photos, and a calm place to buy at the end.
// It is built entirely from what the product already has, so switching it on needs no extra writing.
import React, { useMemo, useRef, useState } from 'react';
import { ChevronLeft, Gauge, Gem, Layers, Ruler, ShieldCheck, Sparkles, Wrench, Zap } from 'lucide-react';
import LazyModelViewer from '../LazyModelViewer.jsx';
import BuyBox from './BuyBox.jsx';
import { ProductTile } from './Home.jsx';
import { BuyBar, ProductFacts } from './ProductPage.jsx';
import { categoryName, gridCols, priceTag } from './api.js';
import { useChosenOption } from './commerce.js';
import { Picture, Reveal, reducedMotion, useScrollProgress } from './ui.jsx';

const cellIcons = [Sparkles, ShieldCheck, Wrench, Ruler, Layers, Zap, Gauge, Gem];
const cellShape = (index, total) => total <= 2 ? 'big' : index < 2 ? 'big' : (total - 2) % 3 === 1 && index === total - 1 ? 'wide' : '';

function LaunchHero({ product }) {
  const hero = useRef(null), orbit = useRef(0);
  // One and a bit turns of the model across the pinned stretch.
  useScrollProgress(hero, 'pin', (progress) => { orbit.current = reducedMotion() ? 0 : progress * Math.PI * 2.4; });
  const config = { ...(product.viewer_config || {}), auto_rotate: false };
  const beta = product.purchase_availability === 'beta';
  // The version picked further down the page is the one turning up here.
  const modelUrl = useChosenOption(product)?.model_url || product.model_url;
  return <section className="launch-hero" ref={hero}>
    <div className="launch-hero-pin">
      <div className="launch-stage" aria-hidden="true">
        {modelUrl
          ? <LazyModelViewer key={`${product.slug}:${modelUrl}`} modelUrl={modelUrl} config={config} orbitRef={orbit} showControls={false} showViewAngle={false} ariaLabel="" />
          : <Picture src={product.image} alt="" sizes="100vw" priority />}
      </div>
      <div className="launch-title">
        <span className="shop-eyebrow">{beta ? 'Coming soon' : `New · ${categoryName(product.category)}`}</span>
        <h1>{product.name}</h1>
        {product.subtitle && <p>{product.subtitle}</p>}
      </div>
      <span className="launch-cue">Scroll</span>
    </div>
  </section>;
}

function Statement({ text }) {
  const section = useRef(null);
  // The pitch: the opening sentence or two, kept short enough to read as one thought.
  const words = useMemo(() => {
    const sentences = String(text || '').replace(/\s+/g, ' ').trim().match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [];
    let pitch = [];
    for (const sentence of sentences) { const next = sentence.trim().split(' '); if (pitch.length && pitch.length + next.length > 26) break; pitch = pitch.concat(next); }
    return pitch.slice(0, 34);
  }, [text]);
  const [lit, setLit] = useState(reducedMotion() ? words.length : 0);
  useScrollProgress(section, 'through', (progress) => {
    const next = Math.round(Math.min(1, Math.max(0, (progress - 0.22) / 0.42)) * words.length);
    setLit((current) => current === next ? current : next);
  });
  if (!words.length) return null;
  return <section className="launch-statement" ref={section}><div className="shop-wrap">
    <p>{words.map((word, index) => <span key={index} className={`word ${index < lit ? 'is-lit' : ''}`}>{word} </span>)}</p>
  </div></section>;
}

function FeatureRow({ detail, image, index }) {
  const media = useRef(null);
  useScrollProgress(media, 'through');
  return <section className="launch-feature shop-wrap">
    <Reveal className="launch-feature-media"><div ref={media} style={{ width: '100%', height: '100%' }}><Picture src={image} alt="" sizes="(max-width: 900px) 100vw, 600px" /></div></Reveal>
    <Reveal className="launch-feature-copy" i={1}>
      <span className="shop-eyebrow">{String(index + 1).padStart(2, '0')}</span>
      {detail.title && <h2>{detail.title}</h2>}
      {detail.text && <p>{detail.text}</p>}
      {detail.items.length > 0 && (detail.ordered ? <ol>{detail.items.map((item) => <li key={item}>{item}</li>)}</ol> : <ul>{detail.items.map((item) => <li key={item}>{item}</li>)}</ul>)}
    </Reveal>
  </section>;
}

function Film({ src }) {
  const frame = useRef(null);
  useScrollProgress(frame, 'through');
  return <div className="launch-video" ref={frame}><video src={src} autoPlay muted loop playsInline preload="metadata" /></div>;
}

function stats(product) {
  const out = [];
  if (product.purchase_availability !== 'beta') out.push({ value: priceTag(product.price_cents, product.currency), label: 'Ready to install' });
  const lead = /^(\d+(?:\s*[-–]\s*\d+)?)\s*(.+)$/.exec(String(product.lead_time || '').trim());
  if (lead) out.push({ value: lead[1].replace(/\s+/g, ''), label: lead[2].toLowerCase() });
  const generations = product.fitment?.generations || [];
  if (generations.length) out.push({ value: generations.join(' · '), label: `${product.fitment.platform || 'Fitment'} ${generations.length > 1 ? 'generations' : 'generation'}` });
  return out;
}

export default function Launch({ product, content, related = [], payment, cart, onBack, onOpen, onPrefetch, onPrivacy }) {
  const hero = useRef(null), close = useRef(null);
  const features = product.features || [];
  const numbers = stats(product);
  // Each detail section gets a photo: its own if it has one, otherwise the product's photos in turn.
  const photoFor = (detail, index) => detail.image || content.photos[(index + 1) % Math.max(content.photos.length, 1)] || product.image;
  return <article className="launch">
    <div ref={hero}><LaunchHero product={product} /></div>
    <Statement text={product.description} />
    {features.length > 0 && <section className="shop-section"><div className="shop-wrap">
      <Reveal className="shop-section-head"><span className="shop-eyebrow">Why it works</span><h2 className="shop-title">Thought through, down to the last layer.</h2></Reveal>
      <div className="launch-bento">
        {features.map((feature, index) => { const Icon = cellIcons[index % cellIcons.length]; return <Reveal key={feature} i={index % 3} className={`launch-cell ${cellShape(index, features.length)}`}><Icon /><strong>{feature}</strong></Reveal>; })}
      </div>
    </div></section>}
    {content.details.map((detail, index) => <FeatureRow key={detail.id || index} detail={detail} image={photoFor(detail, index)} index={index} />)}
    {numbers.length > 0 && <section className="shop-section"><div className="shop-wrap launch-stats">
      {numbers.map((item, index) => <Reveal key={item.label} i={index} className="launch-stat"><strong>{item.value}</strong><span>{item.label}</span></Reveal>)}
    </div></section>}
    {content.videos.map((src) => <section key={src} className="shop-section" style={{ paddingTop: 0 }}><div className="shop-wrap"><Film src={src} /></div></section>)}
    {content.photos.length > 1 && <section style={{ paddingBottom: 'clamp(72px, 11vw, 150px)' }}><div className="shop-gallery">{content.photos.map((path) => <Picture key={path} src={path} alt={product.name} sizes="(max-width: 900px) 78vw, 720px" />)}</div></section>}
    <section className="shop-section" ref={close}><div className="shop-wrap launch-close">
      <Reveal as="span" className="shop-eyebrow">{product.purchase_availability === 'beta' ? 'Almost here' : 'Make it yours'}</Reveal>
      <Reveal as="h2" i={1}>{product.name}</Reveal>
      <Reveal i={2} className="shop-price">
        {product.purchase_availability !== 'beta' && <span>{priceTag(product.price_cents, product.currency)}</span>}
        {product.purchase_availability === 'beta' && <span className="shop-badge">Beta · coming soon</span>}
      </Reveal>
      <Reveal i={3} className="launch-close-buy" style={{ display: 'grid', gap: 18 }}>
        <ProductFacts product={product} />
        <BuyBox product={product} payment={payment} cart={cart} onPrivacy={onPrivacy} />
      </Reveal>
      <button type="button" className="shop-link shop-back" onClick={onBack} style={{ marginTop: 20 }}>All products<ChevronLeft /></button>
    </div></section>
    {related.length > 0 && <section className="shop-section" style={{ paddingTop: 0 }}><div className="shop-wrap">
      <Reveal className="shop-section-head"><span className="shop-eyebrow">More from 3DLabs</span><h2 className="shop-title">You might also like.</h2></Reveal>
      <div className="shop-grid" style={{ '--cols': gridCols(related.length) }}>{related.map((item, index) => <ProductTile key={item.id} product={item} onOpen={onOpen} onPrefetch={onPrefetch} i={index} />)}</div>
    </div></section>}
    <BuyBar product={product} payment={payment} cart={cart} onPrivacy={onPrivacy} watch={hero} until={close} />
  </article>;
}
